<script lang="ts">
    import { defineComponent, h, PropType } from 'vue';

    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import { useUserStore } from '../../../auth/store/user';
    import { UserState } from '../../../auth/types';

    // this guard will provide disable status via slot scope (isDisabledByRbac) if user not have the required features
    // by default as long as one required feature is missing, the isDisabledByRbac will be true
    // if requiredAny is turned on, then isDisabledByRbac will only be true if all required features are missing
    // think as we enable the component as long as at least one required feature is present when requiredAny is on
    export default defineComponent({
        name: 'RbacDisableGuard',
        // See RbacDisplayGuard.vue for the full explanation: @vue/compat's RENDER_FUNCTION
        // compat (on by default under MODE 2) used to wrap this zero-arg render() as
        // legacy, turning `this.$slots` into an auto-invoking Proxy and breaking the
        // `$slots.default(...)` call below in the real running app, not just tests
        // (PRODUCT-26110). Opted out via the documented per-component escape hatch.
        // @vue/compat was removed in PRODUCT-26135; this option now survives as an inert
        // leftover, which plain Vue 3 ignores.
        compatConfig: { RENDER_FUNCTION: false },
        props: {
            features: {
                type: Array as PropType<RbacFeature[]>,
                required: true,
            },
            relyOnLoadingUserPermissions: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            requiredAny: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        computed: {
            userStore(): UserState {
                return useUserStore();
            },
            isLoadingUserPermissions(): boolean {
                return false;
            },
            userFeatures(): Record<RbacFeature, boolean> | null {
                return this.userStore.currentRpRbacFeatures;
            },
            isDisabled(): boolean {
                if (this.requiredAny) {
                    // disable if all features are missing. in other word, enable if user has any features
                    return this.features.every((feature) => !this.userFeatures?.[feature]);
                }
                // disable if there is any missing feature. in other word, enable if user has all features
                return this.features.some((feature) => !this.userFeatures?.[feature]);
            },
        },
        render() {
            const { isLoadingUserPermissions, isDisabled, relyOnLoadingUserPermissions, $slots } = this as any;
            if (isLoadingUserPermissions && relyOnLoadingUserPermissions) {
                return h('div', 'Loading...');
            }
            // Pass data to the default slot, similar to v-bind="scope" in template syntax
            const children = $slots.default({ isDisabledByRbac: isDisabled });
            // A slot call always hands back an array. Returning it unchanged makes this
            // component's root a fragment, and a caller's class or attributes then have no
            // single element to land on -- Vue drops them and warns. Almost every caller
            // wraps one element, so hand that one back directly and the passthrough works.
            return Array.isArray(children) && children.length === 1 ? children[0] : children;
        },
    });
</script>
